import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react'
import styled, { css, keyframes } from 'styled-components'
import { debounce } from 'lodash'

// Components
import Icon from '../icon/Icon'

// Utils
import { useAppState } from '../../context/AppContext'
import {
  useLayoverStack,
  DEFAULT_LAYOVER_WIDTH,
} from '../../context/LayoverStackContext'
import { useDiscardGuard } from '../../hooks/useDiscardGuard'

// Styles
import { hsla } from '../../styles/colors'
import { mqFrom, mqTo, sizes } from '../../styles/mediaQueries'
import { shadows, fastDuration } from '../../styles/effects'

// Panel slide duration; kept local so it doesn't touch the shared slowDuration token
const PANEL_SLIDE_DURATION = 320
// Each underlying panel shifts this many px left per layer stacked above it
const SHIFT_PER_LAYER = 12

// Ref-count of open layovers, so a stacked layer popping doesn't release the
// body-scroll lock while a layer beneath it is still open.
let bodyLockCount = 0

// Shares close handlers + stack position with descendants of `content` (e.g. the
// X / back buttons in StickyHeaderLayoverModal) so every close path plays the
// exit transition without touching the consumers.
export const LayoverModalContext = React.createContext<
  | { requestClose: () => void; closeAll: () => void; isBottom: boolean }
  | undefined
>(undefined)

// Types
interface IProps {
  className?: string
  content: React.ReactNode
  closeModal: () => void
  onClick?: () => void
  // When true, every close path (X, backdrop, back) asks the user to confirm
  // discarding unsaved changes before closing. Defaults to closing straight away.
  isDirty?: boolean
}

const LayoverModal: React.FC<IProps> = ({
  className,
  content,
  closeModal,
  onClick,
  isDirty,
}) => {
  const { theme } = useAppState()

  // Mirror isDirty into a ref so the stable close callbacks always read the
  // latest value without being recreated on every keystroke.
  const guardDiscard = useDiscardGuard()
  const dirtyRef = useRef(false)
  dirtyRef.current = !!isDirty

  // Register in the global stack so this layer knows its depth (shift + dim)
  // and any layer can close the whole stack.
  const stack = useLayoverStack()
  // Grab one stable id on first render only (nextId is >= 1, so 0 = uninitialised)
  const idRef = useRef(0)
  if (idRef.current === 0 && stack) idRef.current = stack.nextId()

  // Delay the actual unmount so the exit transition can play out
  const [isClosing, setIsClosing] = useState(false)
  const closingRef = useRef(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout>>()

  // The unguarded close: plays the exit transition and unmounts. Registered to
  // the stack so closeAll can force-close every layer with a single confirm.
  const forceClose = useCallback(() => {
    if (closingRef.current) return // guard against double close (overlay + X)
    closingRef.current = true
    setIsClosing(true)
    // Flag the stack so the layers beneath shift back in sync with this
    // slide-out, instead of only after it unmounts.
    stack?.markClosing(idRef.current)
    closeTimer.current = setTimeout(closeModal, PANEL_SLIDE_DURATION)
  }, [closeModal, stack])

  // Guarded entry points: confirm a discard when dirty, otherwise close through.
  // requestClose pops this layer (backdrop/back); closeAll drops the whole stack (X).
  const requestClose = useCallback(
    () => guardDiscard(dirtyRef.current, forceClose),
    [guardDiscard, forceClose]
  )
  const closeAll = useCallback(
    () => guardDiscard(dirtyRef.current, stack?.closeAll ?? forceClose),
    [guardDiscard, stack, forceClose]
  )

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current)
    },
    []
  )

  const forceCloseRef = useRef(forceClose)
  forceCloseRef.current = forceClose

  // useLayoutEffect so registration (and the resulting depth recompute for every
  // layer) is flushed before paint — a newly stacked layer never paints a frame
  // at the wrong position, so the dim/shift never flicker.
  useLayoutEffect(() => {
    if (!stack) return
    const id = idRef.current
    // Register a stable indirection to the unguarded close so closeAll always
    // calls the latest forceClose without re-registering on every render. The
    // single discard confirm lives on closeAll itself, not per layer.
    stack.register(id, () => forceCloseRef.current())
    return () => stack.unregister(id)
  }, [])

  const layers = stack?.stack ?? []
  const index = layers.findIndex((l) => l.id === idRef.current)
  // Count only layers above that aren't closing, so the shift starts unwinding
  // the moment the top layer begins its slide-out (not after it unmounts).
  const layersAbove =
    index < 0 ? 0 : layers.slice(index + 1).filter((l) => !l.closing).length
  const isTop = index < 0 || index === layers.length - 1
  const isBottom = index <= 0

  // Desktop-only depth shift: skip it on mobile (full-screen sheets)
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    if (!window) return
    const onResize = () => setIsMobile(window.innerWidth < sizes.M)
    window.addEventListener('resize', onResize)
    onResize()
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const contextValue = useMemo(
    () => ({
      requestClose,
      closeAll,
      isBottom,
    }),
    [requestClose, closeAll, isBottom]
  )

  // Make modal resizeable — width is shared across the stack so resizing any
  // layer (the handle only renders on the top one) resizes them all in sync,
  // and a layer opening on top inherits the current size.
  const [localWidth, setLocalWidth] = useState(DEFAULT_LAYOVER_WIDTH)
  const width = stack ? stack.width : localWidth
  const setWidth = stack ? stack.setWidth : setLocalWidth
  const modalRef = useRef<HTMLDivElement | null>(null)
  // Tracks whether the current press started inside the modal card. A drag-select
  // that begins in an input and ends on the backdrop fires a synthetic click on
  // the backdrop (the nearest common ancestor) — without this guard that would
  // close the modal. We only treat the click as an outside-click when the press
  // also started outside the card.
  const pressStartedInside = useRef(false)
  const isResizing = useRef(false)
  const initialX = useRef(0) // initial X position of the mouse when resizing starts
  const initialWidth = useRef(0) // initial width when resizing starts

  const handleMouseDown = (e: any) => {
    e.preventDefault() // Prevent the mousedown from starting a text selection
    document.body.style.userSelect = 'none' // Disable text selection while dragging
    isResizing.current = true
    // Tell the stack a drag is underway so every layer drops its width transition
    // and follows the cursor instantly instead of easing behind it.
    stack?.setResizing(true)
    initialX.current = e.clientX
    initialWidth.current =
      (modalRef?.current && modalRef.current.offsetWidth) || 0
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleMouseMove = useCallback(
    debounce((e) => {
      if (isResizing.current) {
        const newWidth = initialWidth.current + (initialX.current - e.clientX) // Calculate new width based on mouse movement

        if (newWidth > DEFAULT_LAYOVER_WIDTH && newWidth < window.innerWidth) {
          setWidth(newWidth)
        }
      }
    }, 10),
    []
  )

  const handleMouseUp = () => {
    isResizing.current = false
    stack?.setResizing(false)
    document.body.style.userSelect = '' // Restore text selection after dragging

    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  useEffect(() => {
    return () => {
      document.body.style.userSelect = '' // Reset in case the modal unmounts mid-drag
      stack?.setResizing(false) // Clear the drag flag if we unmount mid-drag
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove])

  useEffect(() => {
    // Prevent body scroll while any layover is open (ref-counted across the stack)
    const body = document.getElementsByTagName('body')[0]
    if (!body) return
    bodyLockCount += 1
    body.style.overflow = 'hidden'

    return () => {
      bodyLockCount -= 1
      if (bodyLockCount <= 0) {
        bodyLockCount = 0
        body.style.overflow = 'initial'
      }
    }
  }, [])

  return (
    <LayoverModalContext.Provider value={contextValue}>
      <Layover
        className={className}
        onMouseDown={(e) => {
          pressStartedInside.current = !!modalRef.current?.contains(
            e.target as Node
          )
        }}
        onClick={() => {
          // Clicking the backdrop pops just this (top) layer, like a back step,
          // rather than dismissing the whole stack.
          if (!pressStartedInside.current) requestClose()
        }}
        isClosing={isClosing}
        isBottom={isBottom}
      >
        <ShiftWrapper layersAbove={layersAbove} isMobile={isMobile}>
          <Modal
            width={width}
            isClosing={isClosing}
            resizing={!!stack?.isResizing}
            ref={modalRef}
            onClick={(e) => {
              e.stopPropagation()
              if (onClick) onClick()
            }}
          >
            {content}
            {/* Resize handle only on the top layer; it drives the shared width. */}
            {isTop && (
              <Resizer onMouseDown={handleMouseDown}>
                <StyledIcon
                  type="Dash"
                  color={theme.colors.textLight}
                  size={24}
                />
              </Resizer>
            )}
          </Modal>
        </ShiftWrapper>
      </Layover>
    </LayoverModalContext.Provider>
  )
}

export default LayoverModal

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`
const fadeOut = keyframes`from { opacity: 1; } to { opacity: 0; }`
const sheetIn = keyframes`from { transform: translateY(100%); } to { transform: translateY(0); }`
const sheetOut = keyframes`from { transform: translateY(0); } to { transform: translateY(100%); }`
const slideInRight = keyframes`from { transform: translateX(100%); } to { transform: translateX(0); }`
const slideOutRight = keyframes`from { transform: translateX(0); } to { transform: translateX(100%); }`

const Layover = styled.div<{ isClosing: boolean; isBottom: boolean }>`
  // Tables scroll inside the layover, so their header doesn't stick to the page
  --table-header-position: static;
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  z-index: 1050;
  overflow: hidden;

  /* Only the bottom (first-opened) layer owns the dim, so it's established once
     and never flickers as layers stack/unstack on top. It lives on a
     pseudo-element so its opacity fade stays isolated to the backdrop — the card
     only slides, it never fades with it. */
  ${({ isBottom, isClosing, theme }) =>
    isBottom &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ${hsla(theme.colors.text, 0.33)};
        animation: ${isClosing ? fadeOut : fadeIn} ${fastDuration}ms ease-in-out
          forwards;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation-duration: ${fastDuration / 4}ms;
    }
  }
`

const ShiftWrapper = styled.div<{ layersAbove: number; isMobile: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  /* Desktop-only depth shift: each layer beneath one or more others slides left
     to reveal the stack. Lives on its own element so this transform doesn't
     collide with the slide-in/out keyframes on Modal. */
  ${({ layersAbove, isMobile }) =>
    !isMobile &&
    css`
      transform: translateX(-${layersAbove * SHIFT_PER_LAYER}px);
      transition: transform ${PANEL_SLIDE_DURATION}ms ease-in-out;
    `}
`

const Modal = styled.div<{
  width: number
  isClosing: boolean
  resizing: boolean
}>`
  background-color: ${({ theme }) => theme.colors.lightest};
  right: 0;
  top: 32px;
  position: absolute;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 12px 12px 0 0;
  box-shadow: ${shadows.depthFour};
  padding: 16px;
  height: calc(100vh - 32px);
  overflow-y: scroll;

  /* Duration/easing set once via longhand so every mqFrom.M block only needs the
     single keyframe interpolation it supports. */
  animation-duration: ${PANEL_SLIDE_DURATION}ms;
  animation-timing-function: ease-in-out;
  animation-fill-mode: forwards;

  ${({ width, isClosing, resizing }) => css`
    /* Mobile: rise up from the bottom (sheet); desktop overrides to slide in from right */
    animation-name: ${isClosing ? sheetOut : sheetIn};

    /* Ease width changes that come from stacking a wider layer, so the panel
       behind grows in step (and in parallel) with the one sliding in on top.
       Lives outside the media query because mqFrom only forwards one
       interpolation; width only actually changes at >=M (mobile stays 100%), so
       this is inert there. Zeroed mid resize-drag so the panel tracks the cursor. */
    transition: width ${resizing ? 0 : PANEL_SLIDE_DURATION}ms ease-in-out;

    ${mqFrom.M`
        width: ${width}px;
      `}

    ${mqFrom.M`
      border-radius: 0;
      top: 0;
      padding: 40px;
      height: 100vh;
      animation-name: ${isClosing ? slideOutRight : slideInRight};
    `}
  `}

  @media (prefers-reduced-motion: reduce) {
    animation-name: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)};
    animation-duration: ${fastDuration / 4}ms;
    transition: none;
  }
`

/* The panel is the scroll container, so an absolute handle would scroll away with
   the content. Sticky to the middle of the visible panel instead; zero height so
   it takes no room in the column, with the icon centred on that line. */
const Resizer = styled.div`
  position: sticky;
  top: 50%;
  bottom: 50%;
  flex-shrink: 0;
  align-self: flex-start;
  /* Back over the panel's desktop padding, onto its left edge */
  margin-left: -40px;
  height: 0;
  width: 24px;
  cursor: ew-resize;
  display: flex;
  align-items: center;
  justify-content: center;

  ${mqTo.M`
    display: none;
  `}
`

const StyledIcon = styled(Icon)`
  transform: rotate(90deg);
`
